import { type ReactNode } from 'react';
import { useT } from '../../lib/i18n';
import { useSession, useUi } from '../../lib/store';
import type { Site } from '../../lib/types';

/**
 * Chrome around every screen: brand header, language switch, account menu.
 *
 * The header stays sticky because the booking flow is long on mobile and the
 * customer needs a way back to "my bookings" or out of the flow at any point.
 */

export function PortalShell({
  site,
  slug,
  view,
  onNavigate,
  children,
}: {
  site: Site;
  slug: string;
  view: 'book' | 'bookings';
  onNavigate: (view: 'book' | 'bookings') => void;
  children: ReactNode;
}): JSX.Element {
  const { t, lang } = useT();
  const setLang = useUi((s) => s.setLang);
  const account = useSession((s) => s.account);
  const signOut = useSession((s) => s.signOut);
  const setAuthOpen = useUi((s) => s.setAuthOpen);

  const displayName = lang === 'ar' && site.name_ar ? site.name_ar : site.name;
  const tagline = lang === 'ar' && site.tagline_ar ? site.tagline_ar : site.tagline;

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white/90 backdrop-blur">
        <div className="bk-gutter mx-auto flex w-full max-w-3xl items-center gap-2 py-2.5 sm:gap-3 sm:py-3">
          <button
            onClick={() => onNavigate('book')}
            /* `min-w-0` on both the button and the text block is what lets the
               brand name give way to the actions on a 320px screen instead of
               pushing them off the edge. */
            className="flex min-w-0 shrink items-center gap-2 text-start sm:gap-3"
            aria-label={displayName}
          >
            {site.logo ? (
              <img src={site.logo} alt="" className="h-9 w-9 shrink-0 rounded-xl object-contain" />
            ) : (
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-sm font-bold"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                aria-hidden
              >
                {displayName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-zinc-900">{displayName}</span>
              {/* The tagline is the first thing to go: on a narrow phone the
                  space is worth more to the account actions. */}
              {tagline && <span className="hidden truncate text-xs text-zinc-500 sm:block">{tagline}</span>}
            </span>
          </button>

          <div className="ms-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
            <button
              onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
              className="bk-btn bk-btn-sm bk-btn-quiet"
              aria-label={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            >
              {lang === 'ar' ? 'EN' : 'ع'}
            </button>

            {account ? (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <button
                  onClick={() => onNavigate(view === 'bookings' ? 'book' : 'bookings')}
                  className="bk-btn bk-btn-sm bk-btn-ghost"
                >
                  {view === 'bookings' ? t('book_now') : t('my_bookings')}
                </button>
                <button
                  onClick={() => signOut(slug)}
                  className="bk-btn bk-btn-sm bk-btn-quiet"
                  title={t('sign_out')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l-5-5 5-5M5 12h12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="bk-sr">{t('sign_out')}</span>
                </button>
              </div>
            ) : (
              <button onClick={() => setAuthOpen(true)} className="bk-btn bk-btn-sm bk-btn-ghost">
                {t('sign_in')}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="bk-gutter mx-auto w-full max-w-3xl flex-1 pb-8 pt-5">{children}</main>

      <footer className="bk-gutter mx-auto w-full max-w-3xl pb-6 text-center text-xs text-zinc-400">
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {site.contact_phone && (
            <a href={`tel:${site.contact_phone}`} className="hover:text-zinc-600">
              {site.contact_phone}
            </a>
          )}
          {site.terms_url && (
            <a href={site.terms_url} target="_blank" rel="noreferrer" className="hover:text-zinc-600">
              {lang === 'ar' ? 'الشروط' : 'Terms'}
            </a>
          )}
          {site.privacy_url && (
            <a href={site.privacy_url} target="_blank" rel="noreferrer" className="hover:text-zinc-600">
              {lang === 'ar' ? 'الخصوصية' : 'Privacy'}
            </a>
          )}
        </div>
      </footer>
    </div>
  );
}

/**
 * A labelled progress rail across the booking steps.
 *
 * Below `sm` only the bars are drawn, with the current step named once
 * underneath. Five labels sharing a 320px row leave about 55px each, which
 * truncates "Services" and "Confirm" into noise — a single readable "Step 3 of
 * 5 · Time" tells the customer more than five ellipses do. The bars stay
 * tappable at every width, so the shortcut back to an earlier step survives.
 */
export function StepRail({
  steps,
  current,
  onJump,
}: {
  steps: Array<{ key: string; label: string; done: boolean }>;
  current: string;
  onJump: (key: string) => void;
}): JSX.Element {
  const { lang } = useT();
  const index = steps.findIndex((s) => s.key === current);
  const currentStep = index >= 0 ? steps[index] : undefined;

  return (
    <nav aria-label="Booking steps" className="mb-5">
      <ol className="flex items-center gap-1.5">
        {steps.map((step, i) => {
          const active = step.key === current;
          const reachable = step.done || active;
          return (
            <li key={step.key} className="flex min-w-0 flex-1 items-center gap-1.5">
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && onJump(step.key)}
                aria-current={active ? 'step' : undefined}
                aria-label={`${i + 1}. ${step.label}`}
                /* The bar is only 4px tall; the padding is what makes it a
                   44px-tall tap target without changing how it looks. */
                className="group flex min-w-0 flex-1 flex-col gap-1.5 py-2 text-start disabled:cursor-default sm:py-0"
              >
                <span
                  className="h-1 w-full rounded-full transition-colors"
                  style={{ background: active || step.done ? 'var(--accent)' : '#e4e4e7' }}
                />
                <span
                  className={`hidden truncate text-[11px] font-medium sm:block ${
                    active ? 'text-zinc-900' : step.done ? 'text-zinc-500' : 'text-zinc-400'
                  }`}
                >
                  {i + 1}. {step.label}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {currentStep && (
        <p className="m-0 mt-1 text-[11px] font-medium text-zinc-500 sm:hidden">
          {lang === 'ar'
            ? `الخطوة ${index + 1} من ${steps.length} · ${currentStep.label}`
            : `Step ${index + 1} of ${steps.length} · ${currentStep.label}`}
        </p>
      )}
    </nav>
  );
}

export function ScreenHeading({ title, subtitle }: { title: string; subtitle?: string }): JSX.Element {
  return (
    <div className="mb-4">
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  const { t } = useT();
  return (
    <div role="alert" className="bk-card bk-rise mb-4 border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
      <p className="m-0">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="bk-btn bk-btn-ghost mt-3 px-4 py-2 text-xs">
          {t('retry')}
        </button>
      )}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-3" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="bk-skeleton h-20" />
      ))}
    </div>
  );
}
